/**
 * Loan Detection Engine (#131)
 *
 * Identifies loan *principal* flows — money borrowed and principal repaid — so
 * they can be excluded from income/expense totals (they cancel out; only the
 * interest/fee is a real cost). One model for every loan type; per-platform
 * detectors decide what counts as a loan.
 *
 * The accounting rule (universal):
 *   - principal IN  (disbursement) -> a liability, NOT income
 *   - principal OUT (repayment)    -> debt paydown, NOT spending
 *   - interest/fee                 -> the ONLY real expense
 * The first two cancel; fees are derived, not read per-row.
 *
 * Fee derivation has two strategies depending on the data:
 *   A) baked-in fee (Cash App Borrow): fee = total repaid - total borrowed
 *      (the Fee column is blank; you can only know fees in aggregate).
 *   B) rate/amortization (e.g. a mortgage): split principal vs interest from the
 *      rate. (Phase 2 — not implemented here.)
 */

import { isLoanPrincipal } from "./credit-products";

/**
 * Is this row a loan principal flow that must be EXCLUDED from income/expense?
 *
 * Phase 2 (#131): this now DELEGATES to the single source of truth
 * `isLoanPrincipal` in credit-products.ts, so the income/expense math and the
 * loans page share ONE classifier and can never diverge. Same behavior as before
 * (Cash App Borrow, detected by Type "Borrow" or name "Borrowing in Cash App") —
 * just one definition instead of two. The (rawType, name) signature is kept for
 * the existing callers (csv-processor normalize + the DB read-mapper).
 */
export function isLoanPrincipalType(
  rawType: string | undefined | null,
  name?: string | undefined | null
): boolean {
  return isLoanPrincipal({
    // classifyCreditProduct reads csvType/transactionType + name/note.
    csvType: rawType ?? undefined,
    name: name ?? undefined,
    // Fields below aren't needed for classification but satisfy the type.
    type: "debit",
    amount: 0,
    date: "",
  });
}

/**
 * Split a set of loan transactions into borrowed (principal in) vs repaid
 * (principal out) by AMOUNT SIGN, and derive the baked-in fee.
 *
 * Pass amounts as SIGNED (positive = disbursement, negative = repayment).
 * Returns absolute totals plus the derived lifetime fee.
 */
export function summarizeLoanFlows(
  signedAmounts: number[]
): { borrowed: number; repaid: number; fees: number } {
  let borrowed = 0;
  let repaid = 0;
  for (const amt of signedAmounts) {
    if (amt > 0) borrowed += amt;
    else if (amt < 0) repaid += -amt;
  }
  // Strategy A (baked-in): fees are whatever was repaid above what was borrowed.
  // Guard against a negative (still-owing) window so we never show negative fees.
  const fees = Math.max(repaid - borrowed, 0);
  return { borrowed, repaid, fees };
}
